 import React, { useState, useEffect } from 'react';
import { HiClock, HiCheck, HiX, HiMail, HiSearch } from 'react-icons/hi';
import { visiteursService } from '../../services/visiteursService';
import { conferencesService } from '../../services/conferencesService';
import { RiFileExcel2Line } from "react-icons/ri";
import * as XLSX from 'xlsx';
import emailjs from '@emailjs/browser';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { InputText } from 'primereact/inputtext';
import ExpandableText from './ExpandableText';

function ConferenciersPrincipauxManagement({ selectedEdition }) {
  const [conferenciers, setConferenciers] = useState([]);
  const [filtreStatut, setFiltreStatut] = useState('tous');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingConferencier, setEditingConferencier] = useState(null);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [conferences, setConferences] = useState([]);

  const statutOptions = [
    { value: 'en_attente', label: 'En attente', color: '#f59e0b', icon: <HiClock /> },
    { value: 'valide', label: 'Validé', color: '#10b981', icon: <HiCheck /> },
    { value: 'rejete', label: 'Rejeté', color: '#ef4444', icon: <HiX /> },
    { value: 'present', label: 'Présent', color: '#8b5cf6', icon: <HiCheck /> },
    { value: 'email confirme', label: 'Email confirmé', color: '#2563eb', icon: <HiMail /> },
    { value: 'email_envoye', label: 'Email envoyé', color: '#06b6d4', icon: <HiMail /> }
  ];

  const profilLabels = {
    'student': 'Étudiant',
    'professor': 'Professeur',
    'professional': 'Professionnel'
  };

  useEffect(() => {
    if (selectedEdition) {
      loadData();
    }
  }, [selectedEdition]);

  const loadData = async () => {
    if (!selectedEdition) return;

    try {
      setLoading(true);
      const [visiteursData, conferencesData] = await Promise.all([
        visiteursService.getVisiteurs(selectedEdition),
        conferencesService.getConferencesData(selectedEdition)
      ]);
      const conferenciersData = visiteursData.filter(v =>
        v.typeParticipation === 'conferencier_principal' || v.categorie === 'conferencier'
      );
      setConferenciers(conferenciersData);
      setConferences(conferencesData.conferences || []);
    } catch (error) {
      console.error('Error loading data:', error);
      setConferenciers([]);
      setConferences([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeStatut = async (id, nouveauStatut) => {
    try {
      await visiteursService.updateVisiteurStatus(id, nouveauStatut);
      setConferenciers(prev => prev.map(c =>
        c.id === id
          ? {
            ...c,
            statut: nouveauStatut,
            referenceParticipation: (nouveauStatut === 'valide' && !c.referenceParticipation)
              ? `GSIDA${Date.now()}`
              : c.referenceParticipation
          }
          : c
      ));

      if (nouveauStatut === 'present') {
        await handleSendWelcomeEmail(id);
      }

      showSuccessMessage();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Erreur lors de la mise à jour du statut.');
    }
  };

  const handleDeleteConferencier = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette inscription ?')) {
      try {
        await visiteursService.deleteVisiteur(id);
        setConferenciers(prev => prev.filter(c => c.id !== id));
        showSuccessMessage();
      } catch (error) {
        console.error('Error deleting:', error);
        alert('Erreur lors de la suppression.');
      }
    }
  };

  const handleEditConferencier = (c) => {
    setEditingConferencier(c);
  };

  const handleSaveConferencier = async (updatedData) => {
    try {
      await visiteursService.updateVisiteur(updatedData.id, updatedData);
      setConferenciers(prev => prev.map(c =>
        c.id === updatedData.id ? { ...c, ...updatedData } : c
      ));
      setEditingConferencier(null);
      showSuccessMessage();
    } catch (error) {
      console.error('Error updating:', error);
      alert('Erreur lors de la modification.');
    }
  };

  const handleMoveUp = async (id) => {
    const speakers = [...conferenciers].sort((a,b) => (a.display_order || 0) - (b.display_order || 0));
    const index = speakers.findIndex(s => s.id === id);
    if (index <= 0) return;

    // Swap
    [speakers[index - 1], speakers[index]] = [speakers[index], speakers[index - 1]];

    // Recalculate display orders
    const updated = speakers.map((s, idx) => ({ ...s, display_order: idx }));

    setConferenciers(updated);

    try {
      // Save order to database
      await Promise.all(updated.map(s => 
        visiteursService.updateVisiteur(s.id, { display_order: s.display_order })
      ));
      showSuccessMessage();
    } catch (error) {
      console.error('Error updating order:', error);
    }
  };

  const handleMoveDown = async (id) => {
    const speakers = [...conferenciers].sort((a,b) => (a.display_order || 0) - (b.display_order || 0));
    const index = speakers.findIndex(s => s.id === id);
    if (index === -1 || index >= speakers.length - 1) return;

    // Swap
    [speakers[index], speakers[index + 1]] = [speakers[index + 1], speakers[index]];

    // Recalculate display orders
    const updated = speakers.map((s, idx) => ({ ...s, display_order: idx }));

    setConferenciers(updated);

    try {
      await Promise.all(updated.map(s => 
        visiteursService.updateVisiteur(s.id, { display_order: s.display_order })
      ));
      showSuccessMessage();
    } catch (error) {
      console.error('Error updating order:', error);
    }
  };

  const getConferenciersFiltres = () => {
    let filtered = conferenciers;
    if (filtreStatut !== 'tous') {
      filtered = filtered.filter(c => c.statut === filtreStatut);
    }
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(c =>
        `${c.prenom} ${c.nom}`.toLowerCase().includes(searchLower) ||
        c.email.toLowerCase().includes(searchLower) ||
        c.telephone.includes(searchTerm) ||
        (c.axe || '').toLowerCase().includes(searchLower) ||
        (c.mot_cle || '').toLowerCase().includes(searchLower) ||
        (c.referenceParticipation || '').toLowerCase().includes(searchLower) ||
        (c.informations || '').toLowerCase().includes(searchLower) ||
        (c.modeParticipation || '').toLowerCase().includes(searchLower)
      );
    }
    return filtered;
  };

  const getModeParticipationLabel = (mode) => {
    if (!mode) return 'Non spécifié';

    const normalized = mode.toString().trim().toLowerCase();
    const labels = {
      presentiel: 'Présentiel',
      online: 'En ligne',
      'en ligne': 'En ligne',
      hybride: 'Hybride',
      hybrid: 'Hybride'
    };

    return labels[normalized] || mode;
  };

  const getStatutInfo = (statut) => {
    return statutOptions.find(option => option.value === statut);
  };

  const getStatistiques = () => {
    const total = conferenciers.length;
    const enAttente = conferenciers.filter(c => c.statut === 'en_attente').length;
    const valides = conferenciers.filter(c => c.statut === 'valide').length;
    const rejetes = conferenciers.filter(c => c.statut === 'rejete').length;
    const presents = conferenciers.filter(c => c.statut === 'present').length;
    const emailConfirmes = conferenciers.filter(c => c.statut === 'email confirme').length;
    const emailEnvoyes = conferenciers.filter(c => c.statut === 'email_envoye').length;
    return { total, enAttente, valides, rejetes, presents, emailConfirmes, emailEnvoyes };
  };

  const handleCreateReference = async (id) => {
    try {
      const updated = await visiteursService.createParticipationReference(id);
      setConferenciers(prev => prev.map(c =>
        c.id === id ? { ...c, referenceParticipation: updated.reference_participation } : c
      ));
      showSuccessMessage();
    } catch (error) {
      console.error('Error creating reference:', error);
      alert('Erreur lors de la création de la référence.');
    }
  };

  const handleSendTicket = async (conferencierId) => {
    try {
      const c = conferenciers.find(v => v.id === conferencierId);
      if (!c) return alert("Conférencier introuvable.");

      const to_email = c.email || '';
      const to_name = `${c.prenom || ''} ${c.nom || ''}`.trim();
      const short_name = `${c.prenom || ''} ${c.nom || ''}`.trim();
      const reference = c.referenceParticipation || '';

      const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(reference)}`;
      const confirmLink = `${window.location.origin}/#confirm?ref=${encodeURIComponent(reference)}`;

      const conference = conferences.find(conf => conf.id.toString() === c.conference?.toString());
      const conference_titre = conference ? conference.titre : '';
      const conference_date = conference ? conference.date : '';
      const conference_salle = conference ? conference.salle : '';

      if (!to_email || !to_name || !reference) {
        alert('Impossible d\'envoyer l\'email : données manquantes.');
        return;
      }

      const emailVars = {
        to_email,
        to_name,
        short_name,
        reference,
        qr_code_url: qrCodeUrl,
        confirm_link: confirmLink,
        conference_titre,
        conference_date,
        conference_salle,
      };

      await emailjs.send(
        'service_i9giahi',
        'template_2tcwp6f', 
        emailVars,
        '_UeBfJMtIUI_gP-mW'
      );

      await visiteursService.updateVisiteurStatus(conferencierId, 'email_envoye');
      await visiteursService.markTicketSent(conferencierId);

      setConferenciers(prev => prev.map(v =>
        v.id === conferencierId
          ? { ...v, ticketSent: true, statut: 'email_envoye' }
          : v
      ));
      alert('Ticket envoyé avec succès ! Statut mis à jour vers "Email envoyé".');
      showSuccessMessage();

    } catch (error) {
      console.error('Error sending ticket:', error);
      alert('Erreur lors de l\'envoi du ticket.');
    }
  };

  const handleSendWelcomeEmail = async (conferencierId) => {
    try {
      const c = conferenciers.find(v => v.id === conferencierId);
      if (!c) return;

      const to_email = c.email || '';
      const to_name = `${c.prenom || ''} ${c.nom || ''}`.trim();
      const conference = conferences.find(conf => conf.id.toString() === c.conference?.toString());
      const conference_titre = conference ? conference.titre : '';
      const conference_date = conference ? conference.date : '';
      const conference_salle = conference ? conference.salle : '';

      const emailVars = {
        to_email,
        to_name,
        conference_titre,
        conference_date,
        conference_salle,
      };

      await emailjs.send(
        'service_i9giahi',
        'template_2tcwp6f',
        emailVars,
        '_UeBfJMtIUI_gP-mW'
      );
    } catch (error) {
      console.error('Error sending welcome email:', error);
    }
  };

  const showSuccessMessage = () => {
    setShowSaveSuccess(true);
    setTimeout(() => setShowSaveSuccess(false), 3000);
  };

  const handleExportExcel = () => {
    const filtered = getConferenciersFiltres();
    const data = filtered.map(c => ({
      Nom: `${c.prenom} ${c.nom}`,
      Email: c.email,
      Téléphone: c.telephone,
      Axe: c.axe || 'Non renseigné',
      'Mots Clés': c.mot_cle || 'Non renseigné',
      'Méthode de participation': getModeParticipationLabel(c.modeParticipation),
      'Titre de communication': c.informations || 'Non renseigné',
      Profil: profilLabels[c.profil] || c.profil,
      'Date inscription': c.dateInscription,
      Référence: c.referenceParticipation || '',
      Statut: getStatutInfo(c.statut)?.label || c.statut,
      'Pièce Jointe': c.piece_jointe_url || 'Aucun document',
      'Photo Profil': c.photo_profil_url || 'Aucune photo',
      Langue: c.langue_preference || 'Non spécifiée'
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Conferenciers Principaux');
    XLSX.writeFile(wb, 'conferenciers_principaux.xlsx');
  };

  if (!selectedEdition) {
    return <div className="admin-section">Please select an edition first</div>;
  }

  if (loading) {
    return <div className="admin-section">Loading...</div>;
  }

  const stats = getStatistiques();

  return (
    <div className="admin-section">
      {showSaveSuccess && (
        <div className="save-success-message">
          <HiCheck /> Opération effectuée avec succès !
        </div>
      )}

      <div className="section-header">
        <h3>Statistiques Conférenciers Principaux</h3>
        <button
          onClick={handleExportExcel}
          className="btn-primary"
          style={{ marginLeft: 'auto', fontSize: 'clamp(0.7rem, 1.8vw, 0.9rem)', padding: '0.5em 1.5em', cursor: 'pointer', backgroundColor: '#107c41' }}
        >
          <RiFileExcel2Line size={20} /> Exporter Excel
        </button>
      </div>

      <div className="stats-grid">
        <div className="stat-card"><h4>Total</h4><span className="stat-number">{stats.total}</span></div>
        <div className="stat-card"><h4>En attente</h4><span className="stat-number" style={{ color: '#f59e0b' }}>{stats.enAttente}</span></div>
        <div className="stat-card"><h4>Validés</h4><span className="stat-number" style={{ color: '#10b981' }}>{stats.valides}</span></div>
        <div className="stat-card"><h4>Rejetés</h4><span className="stat-number" style={{ color: '#ef4444' }}>{stats.rejetes}</span></div>
        <div className="stat-card"><h4>Présents</h4><span className="stat-number" style={{ color: '#8b5cf6' }}>{stats.presents}</span></div>
        <div className="stat-card"><h4>Email Confirmés</h4><span className="stat-number" style={{ color: '#2563eb' }}>{stats.emailConfirmes}</span></div>
        <div className="stat-card"><h4>Email Envoyés</h4><span className="stat-number" style={{ color: '#06b6d4' }}>{stats.emailEnvoyes}</span></div>
      </div>

      <div className="visitors-management-header">
        <div className="header-title">
          <h3>Liste des Conférenciers Principaux</h3>
          <span className="visitors-count">{getConferenciersFiltres().length} conférencier(s)</span>
        </div>

        <div className="search-filters-container">
          <div className="search-section">
            <div className="search-wrapper">
              <HiSearch className="search-icon" />
              <input
                type="text"
                placeholder="Rechercher par nom, email, téléphone, axe, mots clés..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="clear-search-btn">
                  <HiX />
                </button>
              )}
            </div>
          </div>
          <div className="filters-section">
            <div className="filter-group">
              <label className="filter-label">Statut</label>
              <select
                value={filtreStatut}
                onChange={(e) => setFiltreStatut(e.target.value)}
                className="filter-select"
              >
                <option value="tous">Tous les statuts</option>
                <option value="en_attente">En attente</option>
                <option value="valide">Validés</option>
                <option value="rejete">Rejetés</option>
                <option value="present">Présents</option>
                <option value="email confirme">Email Confirmés</option>
                <option value="email_envoye">Email Envoyés</option>
              </select>
            </div>
            {(filtreStatut !== 'tous' || searchTerm) && (
              <button
                onClick={() => { setFiltreStatut('tous'); setSearchTerm(''); }}
                className="reset-filters-btn"
              >
                Réinitialiser
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="card">
        <DataTable 
          value={getConferenciersFiltres().sort((a,b) => (a.display_order || 0) - (b.display_order || 0))} 
          paginator 
          rows={20}
          className="p-datatable-sm"
          stripedRows
          showGridlines
          responsiveLayout="scroll"
          scrollable
          tableStyle={{ tableLayout: 'auto' }}
          emptyMessage="Aucun conférencier principal trouvé."
          header={
            <div className="flex justify-content-between align-items-center py-2">
              <span className="p-input-icon-left w-full sm:w-20rem">
                <i className="pi pi-search" style={{ left: '1rem' }} />
                <InputText 
                  type="search" 
                  value={searchTerm} 
                  onChange={(e) => setSearchTerm(e.target.value)} 
                  placeholder="Rechercher..." 
                  className="w-full"
                  style={{ paddingLeft: '2.5rem' }}
                />
              </span>
            </div>
          }
        >
          <Column 
            header="Ordre" 
            body={(rowData, options) => (
              <div className="flex align-items-center gap-2">
                <span style={{ fontWeight: 'bold' }}>{options.rowIndex + 1}</span>
                <div className="flex flex-column">
                  <Button 
                    icon="pi pi-chevron-up" 
                    className="p-button-text p-button-xs p-0" 
                    onClick={() => handleMoveUp(rowData.id)}
                    disabled={options.rowIndex === 0 || searchTerm !== '' || filtreStatut !== 'tous'}
                    style={{ height: '16px' }}
                  />
                  <Button 
                    icon="pi pi-chevron-down" 
                    className="p-button-text p-button-xs p-0" 
                    onClick={() => handleMoveDown(rowData.id)}
                    disabled={options.rowIndex === getConferenciersFiltres().length - 1 || searchTerm !== '' || filtreStatut !== 'tous'}
                    style={{ height: '16px' }}
                  />
                </div>
              </div>
            )}
            style={{ width: '1%', whiteSpace: 'nowrap' }}
          />
          <Column 
            header="Nom" 
            body={(rowData) => <strong>{rowData.prenom} {rowData.nom}</strong>} 
            sortable 
            sortField="nom"
            style={{ whiteSpace: 'nowrap' }}
          />
          <Column 
            header="Contact" 
            body={(rowData) => (
              <div>
                <div style={{ wordBreak: 'break-all', fontSize: '12px' }}>{rowData.email}</div>
                <div style={{ color: '#666', fontSize: '11px' }}>{rowData.telephone}</div>
              </div>
            )}
            style={{ minWidth: '150px' }}
          />
          <Column 
            header="Axe & Mots Clés" 
            body={(rowData) => (
              <div style={{ fontSize: '12px' }}>
                <div><span style={{ color: '#888' }}>Axe:</span> {rowData.axe || 'N/A'}</div>
                <div><span style={{ color: '#888' }}>Mots:</span> {rowData.mot_cle || 'N/A'}</div>
              </div>
            )}
            style={{ minWidth: '150px' }}
          />
          <Column 
            header="Lieu" 
            body={(rowData) => `${rowData.ville || ''}, ${rowData.paye || ''}`} 
            style={{ whiteSpace: 'nowrap' }}
          />
          <Column 
            field="profil" 
            header="Profil" 
            body={(rowData) => profilLabels[rowData.profil] || rowData.profil}
            style={{ width: '1%', whiteSpace: 'nowrap' }}
          />
          <Column 
            field="langue_preference" 
            header="Langue" 
            body={(rowData) => <span style={{ textTransform: 'capitalize' }}>{rowData.langue_preference || '-'}</span>}
            style={{ width: '1%', whiteSpace: 'nowrap' }}
          />
          <Column 
            field="modeParticipation" 
            header="Méthode de participation" 
            body={(rowData) => getModeParticipationLabel(rowData.modeParticipation)}
            style={{ width: '1%', whiteSpace: 'nowrap' }}
          />
          <Column 
            field="informations" 
            header="Titre de communication" 
            body={(rowData) => <ExpandableText text={rowData.informations || ''} maxChars={40} />}
            style={{ minWidth: '180px' }}
          />
          <Column 
            header="Statut" 
            body={(rowData) => (
              <div className="flex flex-column gap-2 py-2">
                <Tag 
                  value={getStatutInfo(rowData.statut)?.label || rowData.statut} 
                  severity={
                    rowData.statut === 'valide' ? 'success' : 
                    rowData.statut === 'rejete' ? 'danger' : 
                    rowData.statut === 'en_attente' ? 'warning' : 'info'
                  }
                  style={{ width: 'fit-content' }}
                />
                <select
                  value={rowData.statut}
                  onChange={(e) => handleChangeStatut(rowData.id, e.target.value)}
                  className="statut-select"
                  style={{ fontSize: '11px', padding: '2px' }}
                >
                  {statutOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            style={{ width: '1%', whiteSpace: 'nowrap' }}
          />
          <Column 
            header="Pièce Jointe" 
            body={(rowData) => (
              rowData.piece_jointe_url ? (
                <Button 
                  icon="pi pi-file-pdf" 
                  label="Voir" 
                  onClick={() => window.open(rowData.piece_jointe_url, '_blank')}
                  className="p-button-outlined p-button-sm"
                />
              ) : <span style={{ fontSize: '11px', color: '#999' }}>Aucun</span>
            )}
            style={{ width: '1%', whiteSpace: 'nowrap' }}
          />
          <Column 
            header="Photo Profil" 
            body={(rowData) => (
              rowData.photo_profil_url ? (
                <img 
                  src={rowData.photo_profil_url} 
                  alt="Profil" 
                  style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '50%', cursor: 'pointer', border: '1px solid #ccc' }}
                  onClick={() => window.open(rowData.photo_profil_url, '_blank')}
                />
              ) : <span style={{ fontSize: '11px', color: '#999' }}>Aucune</span>
            )}
            style={{ width: '1%', whiteSpace: 'nowrap' }}
          />
          <Column 
            header="Actions" 
            body={(rowData) => (
              <div className="flex gap-2">
                <Button icon="pi pi-pencil" onClick={() => handleEditConferencier(rowData)} rounded outlined severity="info" size="small" />
                <Button icon="pi pi-trash" onClick={() => handleDeleteConferencier(rowData.id)} rounded outlined severity="danger" size="small" />
                {rowData.statut === 'valide' && rowData.referenceParticipation && (
                  <Button 
                    icon={rowData.ticketSent ? "pi pi-check" : "pi pi-envelope"} 
                    rounded 
                    severity={rowData.ticketSent ? "success" : "primary"}
                    onClick={() => handleSendTicket(rowData.id)}
                    size="small"
                  />
                )}
              </div>
            )}
            style={{ width: '1%', whiteSpace: 'nowrap' }}
          />
        </DataTable>
      </div>
    </div>
  );
}

function EditConferencierRow({ c, onSave, onCancel }) {
  const [editData, setEditData] = useState(c);
  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <input type="text" value={editData.prenom} onChange={e => setEditData({ ...editData, prenom: e.target.value })} placeholder="Prénom" style={{ fontSize: '12px', padding: '4px' }} />
        <input type="text" value={editData.nom} onChange={e => setEditData({ ...editData, nom: e.target.value })} placeholder="Nom" style={{ fontSize: '12px', padding: '4px' }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <input type="email" value={editData.email} onChange={e => setEditData({ ...editData, email: e.target.value })} placeholder="Email" style={{ fontSize: '12px', padding: '4px' }} />
        <input type="text" value={editData.telephone} onChange={e => setEditData({ ...editData, telephone: e.target.value })} placeholder="Téléphone" style={{ fontSize: '12px', padding: '4px' }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <input type="text" value={editData.axe || ''} onChange={e => setEditData({ ...editData, axe: e.target.value })} placeholder="Axe" style={{ fontSize: '12px', padding: '4px' }} />
        <input type="text" value={editData.mot_cle || ''} onChange={e => setEditData({ ...editData, mot_cle: e.target.value })} placeholder="Mots Clés" style={{ fontSize: '12px', padding: '4px' }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <input type="text" value={editData.ville || ''} onChange={e => setEditData({ ...editData, ville: e.target.value })} placeholder="Ville" style={{ fontSize: '12px', padding: '4px' }} />
        <input type="text" value={editData.paye || ''} onChange={e => setEditData({ ...editData, paye: e.target.value })} placeholder="Pays" style={{ fontSize: '12px', padding: '4px' }} />
      </div>
      <div>
        <select value={editData.profil} onChange={e => setEditData({ ...editData, profil: e.target.value })} style={{ width: '100%', fontSize: '12px', padding: '4px' }}>
          <option value="student">Étudiant</option>
          <option value="professor">Professeur</option>
          <option value="professional">Professionnel</option>
        </select>
      </div>
      <div>
        <select value={editData.langue_preference || ''} onChange={e => setEditData({ ...editData, langue_preference: e.target.value })} style={{ width: '100%', fontSize: '12px', padding: '4px' }}>
          <option value="">Langue</option>
          <option value="francais">Français</option>
          <option value="anglais">Anglais</option>
          <option value="arabe">Arabe</option>
        </select>
      </div>
      <div><span style={{ fontSize: '10px', color: '#666' }}>{editData.referenceParticipation || 'N/A'}</span></div>
      <div>
        <select value={editData.statut} onChange={e => setEditData({ ...editData, statut: e.target.value })} style={{ width: '100%', fontSize: '12px', padding: '4px' }}>
          <option value="en_attente">En attente</option>
          <option value="valide">Validé</option>
          <option value="rejete">Rejeté</option>
          <option value="present">Présent</option>
          <option value="email confirme">Email confirmé</option>
          <option value="email_envoye">Email envoyé</option>
        </select>
      </div>
      <div><textarea value={editData.informations || ''} onChange={e => setEditData({ ...editData, informations: e.target.value })} placeholder="Informations" style={{ width: '100%', fontSize: '12px', minHeight: 40, padding: '4px' }} /></div>
      <div><span style={{ fontSize: '10px', color: '#999' }}>Fichier en lecture seule</span></div>
      <div className="actions" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <button onClick={() => onSave(editData)} className="btn-primary" style={{ fontSize: '12px', padding: '6px' }}>Sauver</button>
        <button onClick={onCancel} className="btn-secondary" style={{ fontSize: '12px', padding: '6px' }}>Annuler</button>
      </div>
    </>
  );
}

export default ConferenciersPrincipauxManagement;
